<template>
  <div>
    <div class="header-row">
      <div>
        <h2>用户管理</h2>
        <p>管理管理员和业务员账号，支持启停、改密和解锁。</p>
      </div>
      <el-button type="primary" @click="openCreate">
        {{ currentUser.role === "super_admin" ? "新增用户" : "新增业务员" }}
      </el-button>
    </div>

    <div class="brand-card filter-card">
      <div class="toolbar-row">
        <el-input v-model="filters.keyword" placeholder="用户名 / 姓名 / 手机号" clearable />
        <el-select v-model="filters.role" placeholder="角色" clearable>
          <el-option v-if="currentUser.role === 'super_admin'" label="管理员" value="admin" />
          <el-option label="业务员" value="sales" />
        </el-select>
        <el-select
          v-if="currentUser.role === 'super_admin'"
          v-model="filters.leader_id"
          placeholder="归属管理员"
          clearable
          filterable
        >
          <el-option v-for="leader in leaders" :key="leader.id" :label="leader.name" :value="leader.id" />
        </el-select>
        <el-select v-model="filters.province_id" placeholder="省份" clearable @change="handleFilterProvinceChange">
          <el-option v-for="item in provinces" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
        <el-select v-model="filters.city_id" placeholder="城市" clearable :disabled="!filters.province_id">
          <el-option v-for="item in filterCities" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
        <el-select v-model="filters.is_active" placeholder="启用状态" clearable>
          <el-option label="启用" :value="true" />
          <el-option label="停用" :value="false" />
        </el-select>
        <el-button type="primary" plain @click="handleQuery">查询</el-button>
      </div>
    </div>

    <div class="brand-card table-card">
      <el-table :data="users" stripe>
        <el-table-column prop="username" label="用户名" min-width="130" />
        <el-table-column prop="name" label="姓名" min-width="110" />
        <el-table-column prop="phone" label="手机号" min-width="130" />
        <el-table-column prop="role" label="角色" width="100">
          <template slot-scope="{ row }">{{ roleText(row.role) }}</template>
        </el-table-column>
        <el-table-column prop="leader_name" label="归属管理员" min-width="120">
          <template slot-scope="{ row }">
            {{ row.role === "sales" ? (row.leader_name || "-") : "-" }}
          </template>
        </el-table-column>
        <el-table-column prop="region" label="区域" min-width="160">
          <template slot-scope="{ row }">
            <div class="region-tags">
              <el-tag
                v-for="item in row.courier_regions"
                :key="`${row.id}-${item.province_id}-${item.city_id}-${item.district_id || 0}-${item.street_id || 0}`"
                size="mini"
                effect="plain"
              >
                {{ item.text }}
              </el-tag>
              <span v-if="!row.courier_regions || !row.courier_regions.length" class="muted-text">未配置</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="is_active" label="启用状态" width="90">
          <template slot-scope="{ row }">
            <el-tag :type="row.is_active ? 'success' : 'info'" size="mini">
              {{ row.is_active ? "启用" : "停用" }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="锁定状态" width="110">
          <template slot-scope="{ row }">
            <el-tag :type="isLocked(row) ? 'danger' : 'success'" size="mini">
              {{ isLocked(row) ? "已锁定" : "正常" }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="password_error_count" label="错误次数" width="90" />
        <el-table-column prop="last_login_ip" label="最近登录IP" min-width="140">
          <template slot-scope="{ row }">{{ row.last_login_ip || "-" }}</template>
        </el-table-column>
        <el-table-column prop="last_login_at" label="最近登录时间" min-width="160">
          <template slot-scope="{ row }">{{ formatDateTime(row.last_login_at) }}</template>
        </el-table-column>
        <el-table-column prop="created_by_name" label="创建人" min-width="120">
          <template slot-scope="{ row }">{{ row.created_by_name || "-" }}</template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" min-width="160">
          <template slot-scope="{ row }">{{ formatDateTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="锁定截止" min-width="160">
          <template slot-scope="{ row }">{{ formatLockedUntil(row.locked_until) }}</template>
        </el-table-column>
        <el-table-column label="操作" min-width="360" fixed="right">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="openEdit(row)">编辑</el-button>
            <el-button size="mini" plain @click="toggleStatus(row)">
              {{ row.is_active ? "停用" : "启用" }}
            </el-button>
            <el-button v-if="canUnlock(row)" size="mini" type="warning" plain @click="handleUnlock(row)">
              解锁账号
            </el-button>
            <el-button
              v-if="currentUser.role === 'super_admin'"
              size="mini"
              type="warning"
              plain
              @click="openPasswordDialog(row)"
            >
              修改密码
            </el-button>
            <el-button v-if="canDelete(row)" size="mini" type="danger" plain @click="removeUser(row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager-row">
        <el-pagination
          background
          layout="total, prev, pager, next, sizes"
          :total="total"
          :page-size="pager.page_size"
          :current-page="pager.page"
          :page-sizes="[10, 20, 50]"
          @current-change="onPageChange"
          @size-change="onSizeChange"
        />
      </div>
    </div>

    <el-dialog
      :title="editingId ? '编辑用户' : '新增用户'"
      :visible.sync="showDialog"
      width="620px"
      :close-on-click-modal="false"
    >
      <el-form :model="form" label-width="100px">
        <el-form-item label="用户名">
          <el-input v-model.trim="form.username" :disabled="!!editingId" />
        </el-form-item>
        <el-form-item label="姓名">
          <el-input v-model.trim="form.name" />
        </el-form-item>
        <el-form-item label="手机号">
          <el-input v-model.trim="form.phone" />
        </el-form-item>
        <el-form-item label="身份证号码">
          <el-input v-model.trim="form.id_card_no" />
        </el-form-item>
        <el-form-item v-if="!editingId" label="密码">
          <el-input v-model.trim="form.password" show-password placeholder="请输入登录密码" />
        </el-form-item>
        <el-form-item v-if="!editingId" label="角色">
          <el-select v-model="form.role" style="width: 100%;">
            <el-option v-if="currentUser.role === 'super_admin'" label="管理员" value="admin" />
            <el-option label="业务员" value="sales" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="showLeaderField" label="归属管理员">
          <el-select
            v-model="form.leader_id"
            clearable
            filterable
            style="width: 100%;"
            :disabled="currentUser.role === 'admin'"
          >
            <el-option v-for="leader in leaders" :key="leader.id" :label="leader.name" :value="leader.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="负责区域">
          <div class="region-editor">
            <div v-for="(item, index) in form.courier_regions" :key="item.uid" class="region-row">
              <el-select v-model="item.province_id" clearable placeholder="省份" class="region-select" @change="handleRegionProvinceChange(index)">
                <el-option v-for="option in provinces" :key="option.id" :label="option.name" :value="option.id" />
              </el-select>
              <el-select v-model="item.city_id" clearable placeholder="城市" class="region-select" :disabled="!item.province_id" @change="handleRegionCityChange(index)">
                <el-option v-for="option in item.cityOptions" :key="option.id" :label="option.name" :value="option.id" />
              </el-select>
              <el-select v-model="item.district_id" clearable placeholder="区县（选填）" class="region-select" :disabled="!item.city_id" @change="handleRegionDistrictChange(index)">
                <el-option v-for="option in item.districtOptions" :key="option.id" :label="option.name" :value="option.id" />
              </el-select>
              <el-select v-model="item.street_id" clearable placeholder="街道/乡镇（选填）" class="region-select" :disabled="!item.district_id">
                <el-option v-for="option in item.streetOptions" :key="option.id" :label="option.name" :value="option.id" />
              </el-select>
              <el-button type="text" class="danger-text" :disabled="form.courier_regions.length === 1" @click="removeRegionRow(index)">删除</el-button>
            </div>
            <el-button size="mini" plain @click="addRegionRow">新增区域</el-button>
            <div class="form-tip">省份和城市必填，区县、街道/乡镇可选；不选下级表示负责所选上级区域。</div>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showDialog = false">取消</el-button>
        <el-button type="primary" @click="submitForm">提交</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="修改密码"
      :visible.sync="showPasswordDialog"
      width="520px"
      :close-on-click-modal="false"
    >
      <el-form :model="passwordForm" label-width="100px">
        <el-form-item label="新密码">
          <el-input v-model.trim="passwordForm.password" show-password placeholder="请输入新密码" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showPasswordDialog = false">取消</el-button>
        <el-button type="primary" @click="submitPassword">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  createUser,
  deleteUser,
  fetchCities,
  fetchDistricts,
  fetchLeaders,
  fetchProvinces,
  fetchStreets,
  fetchUsers,
  unlockUser,
  updateUser,
  updateUserPassword,
} from "../api/orders";
import { getStoredUser } from "../utils/auth";

const USERNAME_PATTERN = /^[A-Za-z0-9]+$/;

const createRegionRow = () => ({
  uid: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  province_id: null,
  city_id: null,
  district_id: null,
  street_id: null,
  cityOptions: [],
  districtOptions: [],
  streetOptions: [],
});

const emptyForm = () => ({
  username: "",
  name: "",
  phone: "",
  id_card_no: "",
  password: "",
  role: "sales",
  leader_id: null,
  courier_regions: [createRegionRow()],
});

export default {
  data() {
    return {
      currentUser: getStoredUser(),
      filters: {
        keyword: "",
        role: "",
        leader_id: "",
        province_id: "",
        city_id: "",
        is_active: "",
      },
      users: [],
      leaders: [],
      provinces: [],
      filterCities: [],
      total: 0,
      pager: { page: 1, page_size: 10 },
      showDialog: false,
      editingId: null,
      currentRole: "",
      form: emptyForm(),
      showPasswordDialog: false,
      passwordTargetId: null,
      passwordForm: { password: "" },
      allowedRegionScopes: [],
    };
  },
  computed: {
    showLeaderField() {
      return this.form.role === "sales" || this.currentRole === "sales";
    },
  },
  mounted() {
    this.loadBaseData();
  },
  methods: {
    roleText(role) {
      if (role === "admin") return "管理员";
      if (role === "sales") return "业务员";
      return role || "-";
    },
    formatDateTime(value) {
      if (!value) return "-";
      return String(value).replace("T", " ").slice(0, 19);
    },
    isLocked(row) {
      return !!(row && row.locked_until && new Date(row.locked_until).getTime() > Date.now());
    },
    formatLockedUntil(value) {
      if (!value || !this.isLocked({ locked_until: value })) return "-";
      return String(value).replace("T", " ").slice(0, 19);
    },
    canUnlock(row) {
      return this.currentUser.role !== "sales" && (this.isLocked(row) || Number(row.password_error_count || 0) > 0);
    },
    canDelete(row) {
      if (row.id === this.currentUser.id) return false;
      if (this.currentUser.role === "super_admin") return row.role !== "super_admin";
      return row.role === "sales";
    },
    async loadBaseData() {
      this.allowedRegionScopes = this.getCurrentUserRegionScopes();
      await Promise.all([this.loadProvinces(), this.loadLeaders()]);
      await this.loadUsers();
    },
    getCurrentUserRegionScopes() {
      if (this.currentUser.courier_regions && this.currentUser.courier_regions.length) {
        return this.currentUser.courier_regions;
      }
      if (this.currentUser.province_id && this.currentUser.city_id) {
        return [{ province_id: this.currentUser.province_id, city_id: this.currentUser.city_id, district_id: null, street_id: null }];
      }
      return [];
    },
    isRegionWithinAdminScope(provinceId, cityId, districtId = null, streetId = null) {
      if (this.currentUser.role !== "admin") return true;
      return this.allowedRegionScopes.some((scope) => {
        if (scope.province_id !== provinceId || scope.city_id !== cityId) return false;
        if (scope.district_id && scope.district_id !== districtId) return false;
        if (scope.street_id && scope.street_id !== streetId) return false;
        return true;
      });
    },
    async loadProvinces() {
      const { data } = await fetchProvinces();
      if (this.currentUser.role === "admin" && this.allowedRegionScopes.length) {
        const ids = new Set(this.allowedRegionScopes.map((item) => item.province_id));
        this.provinces = (data || []).filter((item) => ids.has(item.id));
        return;
      }
      this.provinces = data || [];
    },
    async loadCities(parentId) {
      if (!parentId) return [];
      const { data } = await fetchCities(parentId);
      const cities = data || [];
      if (this.currentUser.role === "admin") {
        const ids = new Set(this.allowedRegionScopes.filter((item) => item.province_id === parentId).map((item) => item.city_id));
        return ids.size ? cities.filter((item) => ids.has(item.id)) : [];
      }
      return cities;
    },
    async loadDistricts(parentId) {
      if (!parentId) return [];
      const { data } = await fetchDistricts(parentId);
      const districts = data || [];
      if (this.currentUser.role === "admin") {
        const scopes = this.allowedRegionScopes.filter((item) => item.city_id === parentId);
        if (scopes.some((item) => !item.district_id)) return districts;
        const ids = new Set(scopes.map((item) => item.district_id));
        return districts.filter((item) => ids.has(item.id));
      }
      return districts;
    },
    async loadStreets(parentId) {
      if (!parentId) return [];
      const { data } = await fetchStreets(parentId);
      const streets = data || [];
      if (this.currentUser.role === "admin") {
        const scopes = this.allowedRegionScopes.filter((item) => item.district_id === parentId);
        if (scopes.some((item) => !item.street_id) || !scopes.length) return streets;
        const ids = new Set(scopes.map((item) => item.street_id));
        return streets.filter((item) => ids.has(item.id));
      }
      return streets;
    },
    async handleFilterProvinceChange() {
      this.filters.city_id = "";
      this.filterCities = await this.loadCities(this.filters.province_id);
    },
    async loadUsers() {
      const params = {
        keyword: this.filters.keyword || undefined,
        role: this.filters.role || undefined,
        leader_id: this.filters.leader_id || undefined,
        province_id: this.filters.province_id || undefined,
        city_id: this.filters.city_id || undefined,
        is_active: this.filters.is_active === "" ? undefined : this.filters.is_active,
        page: this.pager.page,
        page_size: this.pager.page_size,
      };
      const { data } = await fetchUsers(params);
      this.users = (data.items || []).filter((item) => ["admin", "sales"].includes(item.role));
      this.total = data.total || 0;
    },
    async loadLeaders() {
      const { data } = await fetchLeaders();
      this.leaders = data || [];
    },
    handleQuery() {
      this.pager.page = 1;
      this.loadUsers();
    },
    onPageChange(page) {
      this.pager.page = page;
      this.loadUsers();
    },
    onSizeChange(size) {
      this.pager.page_size = size;
      this.pager.page = 1;
      this.loadUsers();
    },
    async openCreate() {
      this.editingId = null;
      this.currentRole = "";
      this.form = emptyForm();
      if (this.currentUser.role === "admin") {
        if (!this.allowedRegionScopes.length) {
          this.$message.warning("当前管理员未配置负责区域，不能新增业务员");
          return;
        }
        const scope = this.allowedRegionScopes[0];
        this.form.leader_id = this.currentUser.id;
        this.form.courier_regions[0].province_id = scope.province_id;
        this.form.courier_regions[0].city_id = scope.city_id;
        this.form.courier_regions[0].district_id = scope.district_id || null;
        this.form.courier_regions[0].street_id = scope.street_id || null;
      }
      this.showDialog = true;
      for (const item of this.form.courier_regions) {
        await this.hydrateRegionRow(item);
      }
    },
    async openEdit(row) {
      this.editingId = row.id;
      this.currentRole = row.role;
      this.form = {
        username: row.username,
        name: row.name,
        phone: row.phone,
        id_card_no: row.id_card_no || "",
        password: "",
        role: row.role,
        leader_id: row.leader_id || null,
        courier_regions: row.courier_regions && row.courier_regions.length
          ? row.courier_regions.map((item) => ({
              uid: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
              province_id: item.province_id,
              city_id: item.city_id,
              district_id: item.district_id || null,
              street_id: item.street_id || null,
              cityOptions: [],
              districtOptions: [],
              streetOptions: [],
            }))
          : [createRegionRow()],
      };
      this.showDialog = true;
      for (const item of this.form.courier_regions) {
        await this.hydrateRegionRow(item);
      }
    },
    addRegionRow() {
      this.form.courier_regions.push(createRegionRow());
    },
    removeRegionRow(index) {
      if (this.form.courier_regions.length === 1) return;
      this.form.courier_regions.splice(index, 1);
    },
    async handleRegionProvinceChange(index) {
      const item = this.form.courier_regions[index];
      item.city_id = null;
      item.district_id = null;
      item.street_id = null;
      item.cityOptions = await this.loadCities(item.province_id);
      item.districtOptions = [];
      item.streetOptions = [];
      this.$forceUpdate();
    },
    async handleRegionCityChange(index) {
      const item = this.form.courier_regions[index];
      item.district_id = null;
      item.street_id = null;
      item.districtOptions = await this.loadDistricts(item.city_id);
      item.streetOptions = [];
      this.$forceUpdate();
    },
    async handleRegionDistrictChange(index) {
      const item = this.form.courier_regions[index];
      item.street_id = null;
      item.streetOptions = await this.loadStreets(item.district_id);
      this.$forceUpdate();
    },
    async hydrateRegionRow(item) {
      item.cityOptions = await this.loadCities(item.province_id);
      if (item.city_id) {
        item.districtOptions = await this.loadDistricts(item.city_id);
      }
      if (item.district_id) {
        item.streetOptions = await this.loadStreets(item.district_id);
      }
    },
    validateForm() {
      if (!this.form.username) {
        this.$message.warning("请输入用户名");
        return false;
      }
      if (!this.editingId && !USERNAME_PATTERN.test(this.form.username)) {
        this.$message.warning("账号只能使用英文字母和数字，英文字母区分大小写");
        return false;
      }
      if (!this.form.name) {
        this.$message.warning("请输入姓名");
        return false;
      }
      if (!this.form.phone) {
        this.$message.warning("请输入手机号");
        return false;
      }
      if (!this.form.id_card_no) {
        this.$message.warning("请输入身份证号码");
        return false;
      }
      if (!this.editingId && !this.form.password) {
        this.$message.warning("请输入密码");
        return false;
      }
      if (this.showLeaderField && !this.form.leader_id) {
        this.$message.warning("请选择归属管理员");
        return false;
      }
      for (const item of this.form.courier_regions) {
        if (!item.province_id || !item.city_id) {
          this.$message.warning("负责区域中省份和城市必填");
          return false;
        }
        if (this.currentUser.role === "admin" && !this.isRegionWithinAdminScope(item.province_id, item.city_id, item.district_id, item.street_id)) {
          this.$message.warning("负责区域只能选择当前管理员自己的负责区域");
          return false;
        }
      }
      return true;
    },
    async submitForm() {
      if (!this.validateForm()) return;
      const payload = {
        username: this.form.username,
        name: this.form.name,
        phone: this.form.phone,
        id_card_no: this.form.id_card_no,
        password: this.form.password || undefined,
        leader_id: this.showLeaderField ? this.form.leader_id : null,
        courier_regions: this.form.courier_regions.map((item) => ({
          province_id: item.province_id,
          city_id: item.city_id,
          district_id: item.district_id || null,
          street_id: item.street_id || null,
        })),
      };
      try {
        if (this.editingId) {
          await updateUser(this.editingId, payload);
          this.$message.success("更新成功");
        } else {
          await createUser({ ...payload, role: this.form.role });
          this.$message.success("创建成功");
        }
        this.showDialog = false;
        this.loadUsers();
      } catch (error) {
        this.$message.error(error.response?.data?.detail || "提交失败");
      }
    },
    async toggleStatus(row) {
      try {
        await updateUser(row.id, { is_active: !row.is_active });
        this.$message.success("状态已更新");
        this.loadUsers();
      } catch (error) {
        this.$message.error(error.response?.data?.detail || "状态更新失败");
      }
    },
    async handleUnlock(row) {
      try {
        await this.$confirm(`确认解锁账号“${row.name || row.username}”吗？`, "提示", { type: "warning" });
        await unlockUser(row.id);
        this.$message.success("账号已解锁");
        this.loadUsers();
      } catch (error) {
        if (error !== "cancel" && error !== "close") {
          this.$message.error(error.response?.data?.detail || "解锁失败");
        }
      }
    },
    openPasswordDialog(row) {
      this.passwordTargetId = row.id;
      this.passwordForm = { password: "" };
      this.showPasswordDialog = true;
    },
    async submitPassword() {
      if (!this.passwordForm.password) {
        this.$message.warning("请输入新密码");
        return;
      }
      try {
        await updateUserPassword(this.passwordTargetId, this.passwordForm.password);
        this.showPasswordDialog = false;
        this.$message.success("密码已更新");
        this.loadUsers();
      } catch (error) {
        this.$message.error(error.response?.data?.detail || "密码修改失败");
      }
    },
    async removeUser(row) {
      try {
        await this.$confirm(`确认删除账号“${row.name || row.username}”吗？`, "提示", { type: "warning" });
        await deleteUser(row.id);
        this.$message.success("删除成功");
        this.loadUsers();
      } catch (error) {
        if (error !== "cancel" && error !== "close") {
          this.$message.error(error.response?.data?.detail || "删除失败");
        }
      }
    },
  },
};
</script>
